import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);
const STORAGE_KEY = "brunchi_cart";
export const EMAIL_KEY = "brunchi_email";
export const rememberEmail = (email, name) => {
  try { if (email) localStorage.setItem(EMAIL_KEY, JSON.stringify({ email, name: name || "" })); } catch {}
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      return s ? JSON.parse(s) : [];
    } catch { return []; }
  });
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const lastSnap = useRef("");
  const [identTick, setIdentTick] = useState(0);
  const identify = (email, name) => { rememberEmail(email, name); setIdentTick((n) => n + 1); };

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);

  // Abandoned-cart snapshot: sync basket to server when we know the shopper's email
  useEffect(() => {
    let who = user ? { email: user.email, name: user.name } : null;
    if (!who) { try { who = JSON.parse(localStorage.getItem(EMAIL_KEY) || "null"); } catch {} }
    if (!who?.email) return;
    const payload = {
      email: who.email, name: who.name || "",
      items: items.map((i) => ({ slug: i.slug, name: i.name, quantity: i.quantity, price_eur: i.price_eur, image: i.image })),
    };
    const sig = JSON.stringify(payload);
    if (sig === lastSnap.current) return;
    const tm = setTimeout(() => {
      lastSnap.current = sig;
      api.post("/cart/snapshot", payload).catch(() => {});
    }, 1500);
    return () => clearTimeout(tm);
  }, [items, user, identTick]);

  const add = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [...prev, {
        product_id: product.id, slug: product.slug, name: product.name,
        price_eur: product.price_eur, image: product.images?.[0], quantity: qty,
      }];
    });
    setOpen(true);
    // Fire-and-forget funnel event
    api.post("/events", {
      name: "cart_add", product_id: product.id,
      value_eur: product.price_eur * qty,
    }).catch(() => {});
    // Meta Pixel + Google Tag
    try {
      if (window.fbq) window.fbq("track", "AddToCart", { content_ids: [product.id], value: product.price_eur * qty, currency: "EUR" });
      if (window.gtag) window.gtag("event", "add_to_cart", { currency: "EUR", value: product.price_eur * qty, items: [{ item_id: product.id, item_name: product.name, price: product.price_eur, quantity: qty }] });
    } catch {}
  };

  const update = (pid, qty) => setItems((prev) =>
    prev.map((i) => i.product_id === pid ? { ...i, quantity: Math.max(1, qty) } : i));

  const remove = (pid) => setItems((prev) => prev.filter((i) => i.product_id !== pid));

  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price_eur * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartContext.Provider value={{ items, open, setOpen, add, update, remove, clear, subtotal, count, identify }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
